import { Box } from "@mui/material";
import ComparePackageInfoCardList from "components/list/ComparePackageInfoCardList";
import { PackageCardListItemProps } from "components/list/PackageCardListItem";
import { Package } from "models/packages/Package";
import { PackageDepthOfCoverage } from "models/packages/PackageDepthOfCoverage";
import { PackageService } from "models/packages/PackageService";
import { CompareRef } from "pages/SelectPackage/SelectPackage";
import { useRef, useState } from "react";
import {
  useGetDepthsQuery,
  useGetServicesQuery,
} from "store/slices/marketplaceApi";

export interface ComparePackageInfoCardContentsProps {
  combinedPackage: Package;
  infoBoxHeight: number;
  compareRefs: CompareRef[];
  setCompareRefs: (compareRefs: CompareRef[]) => void;
}

const ComparePackageInfoCardContents = ({
  combinedPackage,
  infoBoxHeight,
  compareRefs,
  setCompareRefs,
}: ComparePackageInfoCardContentsProps): JSX.Element => {
  const [openItemId, setOpenItemId] = useState("");
  const handleItemToggle = (itemId: string) =>
    setOpenItemId(openItemId === itemId ? "" : itemId);

  const listContainerRef = useRef<HTMLDivElement>(null);
  const serviceContainerRef = useRef<HTMLDivElement>(null);

  const { data: depths = [] } = useGetDepthsQuery();
  const { data: services = [] } = useGetServicesQuery();

  const depthCategories = [...new Set(depths.map((depth) => depth.categoryId))];

  const serviceIds = services.map((service) => service.id);
  const depthOfCoverageBoxHeight = 160;

  const mapDepths = (currentPackage: Package): PackageCardListItemProps[] => {
    return Object.values(currentPackage.depths)
      .filter((depth: PackageDepthOfCoverage) => !!depth)
      .sort((a, b) => {
        return (
          depthCategories.indexOf(a.categoryId) -
          depthCategories.indexOf(b.categoryId)
        );
      })
      .map((depth: PackageDepthOfCoverage) => {
        return {
          name: depth.name,
          description: depth.description,
        } as PackageCardListItemProps;
      });
  };

  const mapServices = (currentPackage: Package): PackageCardListItemProps[] => {
    return Object.values(currentPackage.services)
      .filter((service: PackageService) => !!service)
      .sort((a, b) => {
        return serviceIds.indexOf(a.id) - serviceIds.indexOf(b.id);
      })
      .map((service: PackageService) => {
        return {
          name: service.name,
          description: service.description,
          disclaimer: service.disclaimer,
        } as PackageCardListItemProps;
      });
  };

  return (
    <Box>
      <Box sx={{ height: "230px" }}></Box>
      <Box
        ref={listContainerRef}
        sx={{
          overflow: "hidden",
          height: infoBoxHeight,
        }}>
        <Box ref={serviceContainerRef}>
          <ComparePackageInfoCardList
            openItemId={openItemId}
            handleItemToggle={handleItemToggle}
            title={"Services"}
            items={mapServices(combinedPackage)}
            width={"330px"}
            compareRefs={compareRefs}
            setCompareRefs={setCompareRefs}
          />
        </Box>
        <Box
          sx={{
            ...(mapDepths(combinedPackage).length && {
              height: depthOfCoverageBoxHeight,
              marginTop: "auto",
              overflow: "visible",
            }),
          }}>
          <ComparePackageInfoCardList
            openItemId={openItemId}
            handleItemToggle={handleItemToggle}
            title={"Depth of Coverage"}
            items={mapDepths(combinedPackage)}
            width={"330px"}
            compareRefs={compareRefs}
            setCompareRefs={setCompareRefs}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ComparePackageInfoCardContents;
